import { Chip, Paper, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import type { ScrapMixFilterOption, ScrapMixFilters } from "../models";
import {
  ALL_OPTIONS_LABEL,
  areAllOptionsSelected,
} from "../utils/filterSelection";
import { buildOperatorChipLabels } from "../utils/operatorChipLabels";
import { filterFields } from "./AnalysisFilters";

export type MultiSelectFilterField = (typeof filterFields)[number]["field"];

interface Props {
  filters: ScrapMixFilters;
  options: ScrapMixFilterOption[];
  onRemove: (field: MultiSelectFilterField, value: string) => void;
}

export const ActiveFilterChips = ({ filters, options, onRemove }: Props) => {
  const zones = useMemo(() => {
    const labelsByTypeAndValue = new Map(
      options.map((option) => [
        `${option.FilterType}|${option.Value}`,
        option.Label,
      ]),
    );

    const operatorChipLabels = buildOperatorChipLabels(options);

    return filterFields
      .filter(({ field }) => filters[field].length > 0)
      .map(({ type, field, label }) => ({
        field,
        label,
        allSelected: areAllOptionsSelected(
          options.filter((option) => option.FilterType === type),
          filters[field],
        ),
        chips: filters[field].map((value) => {
          const fullLabel =
            labelsByTypeAndValue.get(`${type}|${value}`) ?? value;
          return {
            value,
            fullLabel,
            label:
              type === "Operator"
                ? (operatorChipLabels.get(value) ?? fullLabel)
                : fullLabel,
          };
        }),
      }));
  }, [filters, options]);

  if (zones.length === 0) return null;

  return (
    <Paper elevation={1} sx={{ p: 1.5 }} data-testid="active-filter-chips">
      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={2}>
        {zones.map((zone) => (
          <Stack
            key={zone.field}
            direction="row"
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            spacing={0.75}
            data-testid={`filter-chip-zone-${zone.field}`}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {zone.label}
            </Typography>
            {zone.allSelected ? (
              <Chip
                size="small"
                label={ALL_OPTIONS_LABEL}
                title={zone.chips.map((chip) => chip.fullLabel).join(", ")}
                onDelete={() =>
                  zone.chips.forEach((chip) => onRemove(zone.field, chip.value))
                }
              />
            ) : (
              zone.chips.map((chip) => (
                <Chip
                  key={chip.value}
                  size="small"
                  label={chip.label}
                  title={chip.fullLabel}
                  onDelete={() => onRemove(zone.field, chip.value)}
                />
              ))
            )}
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};
